import { ReactNode } from 'react';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';

import { FACILITY_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { MemberCheck } from '@pass-frontend/data-access/common/edge';
import { RdsLink } from '@pass-frontend/rally-energon-react';
import { isGovernmentDependentMemberCheck } from '../utility';

interface MemberDetailsProps {
  title?: string;
  memberDetails: MemberCheck;
  info?: ReactNode;
  showNewSearchLink?: boolean;

  hasAccess?: boolean;
  fromLocationAccess?: boolean;
}

const StyledHeaderWrapper = styled.div`
  justify-content: space-between;
`;

const StyledLink = styled(RdsLink)`
  margin-right: -6px;
`;

const GovernmentDependentBanner = () => {
  const { t } = useIntl();

  return (
    <div
      style={{
        background: '#DBF3E5',
        padding: 16,
        fontSize: 14.22,
        marginBottom: 24,
      }}
    >
      <div style={{ color: '#277447' }}>
        <b>{t('facilityWeb.memberDetail.govDepEligible')}</b>
      </div>
      <div>
        <b>{t('facilityWeb.memberDetail.govDepAccompanied')}</b>
      </div>
    </div>
  );
};

export function MemberDetails({
  title,
  memberDetails,
  info,
  showNewSearchLink,
  hasAccess = true,
  fromLocationAccess = false,
}: MemberDetailsProps) {
  const { t } = useIntl();
  const router = useRouter();

  const handleNewSearchClick = (e) => {
    e.preventDefault();
    router?.push(FACILITY_PATHS.DASHBOARD);
  };

  const isGovernmentDependentMember =
    isGovernmentDependentMemberCheck(memberDetails);

  return (
    <>
      <StyledHeaderWrapper className="is-flex">
        <div className="is-rds-h6 has-rds-font-weight-medium">
          {title || t('facilityWeb.memberInfo.title')}
        </div>
        {showNewSearchLink && (
          <StyledLink
            variant="standalone"
            tabIndex={0}
            href={FACILITY_PATHS.DASHBOARD}
            onClick={handleNewSearchClick}
            typographySize="body-2"
          >
            {t('facilityWeb.newMemberSearch')}
          </StyledLink>
        )}
      </StyledHeaderWrapper>
      <div className="has-rds-radius-4 has-rds-p-24 has-rds-mt-16 has-rds-bg-white has-rds-box-shadow-neutral-spread-6">
        {!!info && info}
        {isGovernmentDependentMember && hasAccess && !fromLocationAccess && (
          <GovernmentDependentBanner />
        )}

        <div className="is-rds-body-3 has-rds-font-weight-semibold has-rds-pb-16">
          <p>
            {t('facilityWeb.memberInfo.userCode', {
              userCode: memberDetails?.code,
            })}
          </p>
        </div>
        <div className="is-rds-body-3 has-rds-font-weight-semibold has-rds-pb-16">
          <p>
            {t('facilityWeb.memberInfo.sector', {
              sector: memberDetails?.sector,
            })}
          </p>
        </div>
        <div className="is-rds-body-3 has-rds-font-weight-semibold has-rds-pb-16">
          <p>
            {t('facilityWeb.memberInfo.userTier', {
              userTier: memberDetails?.tierName,
            })}
          </p>
        </div>
        <div className="is-rds-body-3 has-rds-font-weight-semibold has-rds-pb-16">
          <p>
            {t('facilityWeb.memberInfo.firstName', {
              firstName: memberDetails?.firstName,
            })}
          </p>
        </div>
        <div className="is-rds-body-3 has-rds-font-weight-semibold">
          <p>
            {t('facilityWeb.memberInfo.lastName', {
              lastName: memberDetails?.lastName,
            })}
          </p>
        </div>
      </div>
    </>
  );
}
